import {createStyles} from '@mantine/core';
import classNames from 'classnames';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface CollapsibleSidebarProps {
  children: React.ReactNode;
  component?: string;
  initiallyOpen?: boolean;
  className?: string;
  stickyHeader?: React.ReactNode;
  disableScroll?: boolean;
}

/**
 * A reusable collapsible sidebar component with consistent styling and behavior
 */
export function CollapsibleSidebar(props: CollapsibleSidebarProps): JSX.Element {
  const {
    children,
    component = 'CollapsibleSidebar',
    initiallyOpen = true,
    className,
    stickyHeader,
    disableScroll,
  } = props;
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const {classes, cx} = useStyles();

  return (
    <div
      aria-expanded={isOpen}
      {...getTestProps({component, element: 'root'})}
      className={cx(classes.sidebarWrapper, !isOpen && classes.closed, 'hide-on-print', className)}
    >
      <div
        className={cx(classes.tabsContainer, !isOpen && classes.tabsClosed)}
        {...getTestProps({component, element: 'tabsContainer'})}
      >
        {stickyHeader != null && <div className={classes.stickyHeader}>{stickyHeader}</div>}
        <div
          className={classNames(
            'scrollbar-override',
            cx(classes.sidebar, disableScroll && classes.sidebarNoScroll)
          )}
        >
          {children}
        </div>
      </div>
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        className={classes.opener}
        icon={isOpen ? 'chevron-left' : 'chevron-right'}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        size={ButtonSize.MEDIUM}
      />
    </div>
  );
}

const SIDEBAR_WIDTH = 240;
const SIDEBAR_PADDING = 16;

const useStyles = createStyles((theme) => ({
  sidebarWrapper: {
    position: 'relative',
    width: SIDEBAR_WIDTH,
    height: '100%',
    transition: '0.3s ease-in-out',
  },
  closed: {
    width: 20,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    opacity: 1,
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in',
    width: SIDEBAR_WIDTH - SIDEBAR_PADDING,
    transform: 'translate(0px, 0px)',
    height: '100%',
  },
  tabsClosed: {
    opacity: 0,
    transform: `translate(-${SIDEBAR_WIDTH}px, 0px)`,
    width: SIDEBAR_WIDTH - SIDEBAR_PADDING,
    pointerEvents: 'none',
  },
  stickyHeader: {
    width: SIDEBAR_WIDTH,
    // Pushes the header below the absolutely-positioned opener button (top: -10, height: 36px).
    paddingTop: 30,
    paddingRight: SIDEBAR_PADDING,
    background: theme.white,
    flexShrink: 0,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '20px',
    gap: 10,
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    paddingRight: SIDEBAR_PADDING,
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
  },
  sidebarNoScroll: {
    overflowY: 'hidden',
  },
  opener: {
    position: 'absolute',
    top: -10,
    right: 0,
  },
}));
